import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShoppingCart,
  Clock,
  Store,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function AdminHomePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pendingRequests: 0,
    pendingGroups: 0,
    confirmedGroups: 0,
    changedGroups: 0,
    deadlineToday: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [ordersRes, requestsRes, storesRes, productsRes, groupsRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/orders`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/orders/change-requests/batch`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/stores`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/products`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/order-groups`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/delivery-schedules`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);

      const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const requests = Array.isArray(requestsRes.data) ? requestsRes.data : [];
      const storesList = Array.isArray(storesRes.data) ? storesRes.data : [];
      const productsList = Array.isArray(productsRes.data) ? productsRes.data : [];
      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
      const schedules = Array.isArray(schedulesRes.data) ? schedulesRes.data : [];

      // 統計計算
      const pendingRequests = requests.filter((r) => r.status === "pending").length;
      const pendingGroups = groups.filter((g) => !g.confirmed).length;
      const confirmedGroups = groups.filter((g) => g.confirmed && !g.changed).length;
      const changedGroups = groups.filter((g) => g.changed).length;

      // 本日が発注締切 or 変更締切の納品日数（JST基準）
      const todayStr = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo" }).format(new Date());
      const deadlineToday = schedules.filter((s) => {
        const dl = (s.deadline || "").slice(0, 10);
        const cdl = (s.change_deadline || "").slice(0, 10);
        return dl === todayStr || cdl === todayStr;
      }).length;

      setStats({
        pendingRequests,
        pendingGroups,
        confirmedGroups,
        changedGroups,
        deadlineToday,
      });

      // 最新の発注（直近10件）
      const recent = orders
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
      setRecentOrders(recent);

      setStores(storesList);
      setProducts(productsList);
    } catch (error) {
      console.error("ダッシュボードデータの取得に失敗:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStoreName = (storeId) => {
    const store = stores.find((s) => s.id === storeId);
    return store?.name || storeId;
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p.id === productId);
    return product?.name || productId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Welcome hero */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100 rounded-2xl p-6 sm:p-8">
        <div className="relative z-10">
          <p className="text-xs text-emerald-700 mb-1 font-medium">やさまるポータル 管理者</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
            管理者ダッシュボード
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            発注受信状況を一目で確認できます。
          </p>
        </div>
        {/* 装飾イラスト（右側・丘と芽） */}
        <svg
          className="hidden md:block absolute right-0 bottom-0 h-full w-1/2 pointer-events-none"
          viewBox="0 0 400 200"
          preserveAspectRatio="xMaxYMax slice"
          aria-hidden="true"
        >
          <path d="M0 160 Q120 110 260 150 T400 130 L400 200 L0 200 Z" fill="#bbf7d0" opacity="0.55" />
          <path d="M120 175 Q240 130 400 165 L400 200 L120 200 Z" fill="#86efac" opacity="0.55" />
          <g transform="translate(300 70)">
            <path d="M8 90 L8 40" stroke="#4ade80" strokeWidth="4" strokeLinecap="round" />
            <path d="M8 55 C-14 50 -22 28 -20 14 C-4 16 10 34 8 55 Z" fill="#4ade80" opacity="0.85" />
            <path d="M8 44 C26 38 34 18 32 6 C16 8 4 24 8 44 Z" fill="#22c55e" opacity="0.85" />
          </g>
        </svg>
      </div>

      {/* 発注状況サマリ */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-emerald-600" />
          発注状況サマリ
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 未確定 */}
          <button
            data-testid="summary-pending"
            onClick={() => navigate("/admin/action-center")}
            className="text-left group rounded-2xl bg-white border border-amber-200 p-5 shadow-sm transition-all hover:border-amber-400 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-amber-50 shrink-0">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <span className="text-3xl font-bold tabular-nums text-amber-700" data-testid="summary-pending-count">
                {stats.pendingGroups}
              </span>
            </div>
            <div className="mt-3">
              <div className="text-sm font-semibold text-slate-800">未確定</div>
              <div className="text-xs text-slate-500 mt-0.5">デスク未送信の発注グループ</div>
            </div>
          </button>

          {/* 変更あり */}
          <button
            data-testid="summary-changed"
            onClick={() => navigate("/admin/action-center")}
            className={`text-left group rounded-2xl bg-white border p-5 shadow-sm transition-all hover:shadow-md ${
              stats.changedGroups > 0
                ? "border-red-300 hover:border-red-400"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className={`p-2.5 rounded-xl shrink-0 ${stats.changedGroups > 0 ? "bg-red-50" : "bg-slate-50"}`}>
                <AlertTriangle className={`h-5 w-5 ${stats.changedGroups > 0 ? "text-red-600" : "text-slate-400"}`} />
              </div>
              <span
                className={`text-3xl font-bold tabular-nums ${stats.changedGroups > 0 ? "text-red-600" : "text-slate-400"}`}
                data-testid="summary-changed-count"
              >
                {stats.changedGroups}
              </span>
            </div>
            <div className="mt-3">
              <div className="text-sm font-semibold text-slate-800">変更あり（要再確定）</div>
              <div className="text-xs text-slate-500 mt-0.5">確定後にお客様が変更</div>
            </div>
          </button>

          {/* 確定済 */}
          <button
            data-testid="summary-confirmed"
            onClick={() => navigate("/admin/orders")}
            className="text-left group rounded-2xl bg-white border border-emerald-200 p-5 shadow-sm transition-all hover:border-emerald-400 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-emerald-50 shrink-0">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <span className="text-3xl font-bold tabular-nums text-emerald-700" data-testid="summary-confirmed-count">
                {stats.confirmedGroups}
              </span>
            </div>
            <div className="mt-3">
              <div className="text-sm font-semibold text-slate-800">確定済</div>
              <div className="text-xs text-slate-500 mt-0.5">デスク取込対象</div>
            </div>
          </button>

          {/* 本日締切 */}
          <button
            data-testid="summary-deadline"
            onClick={() => navigate("/admin/delivery-schedules")}
            className={`text-left group rounded-2xl bg-white border p-5 shadow-sm transition-all hover:shadow-md ${
              stats.deadlineToday > 0
                ? "border-indigo-300 hover:border-indigo-400"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className={`p-2.5 rounded-xl shrink-0 ${stats.deadlineToday > 0 ? "bg-indigo-50" : "bg-slate-50"}`}>
                <CalendarClock className={`h-5 w-5 ${stats.deadlineToday > 0 ? "text-indigo-600" : "text-slate-400"}`} />
              </div>
              <span
                className={`text-3xl font-bold tabular-nums ${stats.deadlineToday > 0 ? "text-indigo-700" : "text-slate-400"}`}
                data-testid="summary-deadline-count"
              >
                {stats.deadlineToday}
              </span>
            </div>
            <div className="mt-3">
              <div className="text-sm font-semibold text-slate-800">本日締切</div>
              <div className="text-xs text-slate-500 mt-0.5">発注・変更締切の納品日</div>
            </div>
          </button>
        </div>
      </div>

      {/* クイックアクション */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-emerald-600" />
          クイックアクション
        </h2>

        {/* Row 1: 全発注データ */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <button
            data-testid="admin-card-orders"
            onClick={() => navigate("/admin/orders")}
            className="lg:col-span-5 text-left group rounded-2xl p-6 bg-gradient-to-br from-emerald-500 to-green-600 shadow-sm transition-all hover:from-emerald-600 hover:to-green-700 hover:shadow-lg"
          >
            <div className="flex items-center gap-4">
              <div className="p-3.5 rounded-2xl bg-white/20 shrink-0">
                <ShoppingCart className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-2xl font-bold text-white">全発注データ</div>
                <div className="text-sm text-emerald-50 mt-1">すべての発注を確認</div>
              </div>
              <div className="p-2 rounded-full bg-white/20 shrink-0 group-hover:translate-x-1 transition-transform">
                <ChevronRight className="h-5 w-5 text-white" />
              </div>
            </div>
          </button>
        </div>

        {/* Row 2: 小カード */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <button
            data-testid="admin-card-stores"
            onClick={() => navigate("/admin/stores-users")}
            className="text-left group rounded-2xl bg-white border border-slate-200 p-5 shadow-sm transition-all hover:border-emerald-300 hover:shadow-md"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-50 shrink-0">
                <Store className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-lg text-slate-800">店舗・ユーザー</div>
                <div className="text-sm text-slate-500 mt-0.5">店舗と発注者を管理</div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all shrink-0" />
            </div>
          </button>
        </div>
      </div>

      {/* 最新の発注 */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-emerald-600" />
            最新の発注
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/orders")}
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
          >
            すべて見る →
          </Button>
        </div>

        {recentOrders.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center text-slate-500">
            発注データがありません
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                    店舗
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                    商品
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                    数量
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                    納品日
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-600 px-4 py-3">
                    受信日時
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate("/admin/orders")}
                  >
                    <td className="px-4 py-3 text-sm text-slate-800">
                      <div className="flex items-center gap-2">
                        <Store className="h-4 w-4 text-slate-400" />
                        {getStoreName(order.store_id)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800">
                      {getProductName(order.product_id)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800">
                      {order.quantity} {order.unit}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {order.delivery_date}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {order.created_at
                        ? new Date(order.created_at).toLocaleString("ja-JP", {
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
